import { useState, VFC } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  createContributionSearchConditions,
  updateContributionSearchConditions,
} from 'src/api/contribution'
import { ApiResponse } from 'src/api/types'
import { asModal } from 'src/components/hoc/asModal'
import { ContributionSearchConditionsData } from 'src/models/Contribution'
import { MasterData, MasterDataWithFieldType } from 'src/models/MasterData'
import {
  trueblack,
  truewhite,
  _contributionsBackground,
  _contributiontext,
  _formalert,
  _menuicon,
  _profilegreen,
  _profileoverlay,
  _searchbg,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled, { css } from 'styled-components'
import { inversing } from '../../styles/mixins'
import { FilterSettingParams } from './FilterSettingParams'

type ValuesType = {
  title: string
}

export type FilterSettingModalViewProps = {
  selectedFreeWords: string[]
  masterTypeOfPurchases?: MasterData[]
  selectedTypeOfPurchases: string[]
  specialties?: MasterDataWithFieldType[]
  selectedSpecialties: number[]
  budgets?: MasterData[]
  selectedBudgets: string[]
  introductionTimings?: MasterData[]
  selectedIntroductionTimings: string[]
  close: VoidFunction
  searchCondition: ContributionSearchConditionsData | undefined
  onClickFetchContributionSearchConditions: (isCreate: boolean) => void
}

const FilterSettingModalView: VFC<FilterSettingModalViewProps> = ({
  selectedFreeWords,
  masterTypeOfPurchases,
  selectedTypeOfPurchases,
  specialties,
  selectedSpecialties,
  budgets,
  selectedBudgets,
  introductionTimings,
  selectedIntroductionTimings,
  close,
  searchCondition,
  onClickFetchContributionSearchConditions,
}) => {
  const { register, handleSubmit, formState } = useForm<any>({
    defaultValues: {
      title: '',
    },
  })
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [title, setTitle] = useState<string | undefined>(searchCondition?.title)

  const handleOnSubmit: SubmitHandler<ValuesType> = async (values) => {
    let res: ApiResponse<any>
    if (searchCondition)
      res = await updateContributionSearchConditions(
        searchCondition.id,
        values.title,
        JSON.stringify({
          free_word: searchCondition.params?.freeWord,
          type_of_purchase: searchCondition.params?.typeOfPurchase,
          specialties: searchCondition.params?.specialties,
          budget: searchCondition.params?.budget,
          introduction_timing: searchCondition.params?.introductionTiming,
        }),
      )
    else
      res = await createContributionSearchConditions(
        values.title,
        JSON.stringify({
          free_word: selectedFreeWords?.join('|'),
          type_of_purchase: selectedTypeOfPurchases?.join('|'),
          specialties: selectedSpecialties?.join('|'),
          budget: selectedBudgets?.join('|'),
          introduction_timing: selectedIntroductionTimings?.join('|'),
        }),
      )

    if (res.error?.errorMessages) {
      setErrorMessages(res.error.errorMessages)
      setTitle(undefined)
      return
    }
    setErrorMessages([])
    setTitle(undefined)
    onClickFetchContributionSearchConditions(searchCondition ? false : true)
    close()
  }

  return (
    <StyledForm onSubmit={handleSubmit(handleOnSubmit)}>
      <Layout>
        <Body>
          <FilterSettingModalHeader>
            <FilterSettingModalHeaderTitle>
              {searchCondition ? <>フィルタを更新</> : <>フィルタを作成</>}
            </FilterSettingModalHeaderTitle>
          </FilterSettingModalHeader>
          <FilterTitleHeader>
            <FilterTitle>フィルタ名</FilterTitle>
          </FilterTitleHeader>
          <FilterTitleInputArea>
            <FilterTitleInput
              placeholder="検索条件名"
              {...register('title', {
                value: title,
                required: '検索条件名は必須です',
              })}
            />
            <Alert>
              {!!formState.errors.title && (
                <p>{formState.errors.title.message}</p>
              )}
            </Alert>
          </FilterTitleInputArea>
          <FilterParamHeader>
            <FilterParamTitle>絞り込み条件の詳細</FilterParamTitle>
          </FilterParamHeader>
          <FilterParamTable>
            <FilterSettingParams
              title={'フリーワード'}
              selectedItems={
                searchCondition && searchCondition.params?.freeWord
                  ? searchCondition.params.freeWord.split('|')
                  : selectedFreeWords
              }
            />
            <FilterSettingParams
              title={'タイプ'}
              selectedItems={
                searchCondition && searchCondition.params?.typeOfPurchase
                  ? searchCondition.params.typeOfPurchase.split('|')
                  : selectedTypeOfPurchases
              }
              masterData={masterTypeOfPurchases}
            />
            <FilterSettingParams
              title={'分野'}
              selectedItems={
                searchCondition && searchCondition.params?.specialties
                  ? searchCondition.params.specialties.split('|')
                  : selectedSpecialties
              }
              masterData={specialties}
            />
            <FilterSettingParams
              title={'予算'}
              selectedItems={
                searchCondition && searchCondition.params?.budget
                  ? searchCondition.params.budget.split('|')
                  : selectedBudgets
              }
              masterData={budgets}
            />
            <FilterSettingParams
              title={'導入時期'}
              selectedItems={
                searchCondition && searchCondition.params?.introductionTiming
                  ? searchCondition.params.introductionTiming.split('|')
                  : selectedIntroductionTimings
              }
              masterData={introductionTimings}
            />
            <FilterParamButton>
              <CancelButton type="button" onClick={close}>
                キャンセル
              </CancelButton>
              <FilterSettingButton type="submit">
                {searchCondition ? <>保存する</> : <>作成する</>}
              </FilterSettingButton>
            </FilterParamButton>
            <Alert>
              {errorMessages.length > 0 && (
                <p>
                  {errorMessages.map((message) => (
                    <p key={message}>{message}</p>
                  ))}
                </p>
              )}
            </Alert>
          </FilterParamTable>
        </Body>
      </Layout>
    </StyledForm>
  )
}

const modalOverlayStyle = css`
  backdrop-filter: blur(6px) brightness(0.5);
  background-color: ${_profileoverlay}80;
`

export const FilterSettingModal = asModal(FilterSettingModalView, {
  overlayStyle: modalOverlayStyle,
  scrollable: true,
})

const StyledForm = styled.form``

const Layout = styled.div`
  width: 800px;
  box-shadow: 0 5px 15px ${trueblack}80;
  border-radius: 5px;
`

const Body = styled.div`
  background: ${truewhite};
  padding: 32px;
  border-radius: 5px;
`

const FilterSettingModalHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
`

const FilterSettingModalHeaderTitle = styled.div``

const FilterTitleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 700;
`
const FilterTitle = styled.div`
  color: ${_profilegreen};
`

const FilterTitleInputArea = styled.div``

const FilterTitleInput = styled.input`
  background-color: ${_searchbg};
  font-size: 16px;
  width: 100%;
  ::placeholder {
    color: ${_menuicon};
    font-weight: ${fontWeightBold};
  }
  padding: 14px;
`

const FilterParamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  font-size: 16px;
  font-weight: 700;
`
const FilterParamTitle = styled.div`
  color: ${_profilegreen};
`

const FilterParamTable = styled.div`
  display: flex;
  flex-direction: column;
`

const FilterParamButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 40px;
`

const CancelButton = styled.button`
  display: block;
  color: ${_profilegreen};
  width: 200px;
  padding: 16px 0;
  border-radius: 5px;
  text-align: center;
  background: ${_contributionsBackground};

  ${inversing({
    color: _contributiontext,
    background: _contributionsBackground,
  })};
`

const FilterSettingButton = styled.button`
  display: block;
  color: ${truewhite};
  width: 200px;
  padding: 10px 0;
  margin-left: 24px;
  border-radius: 5px;
  border: solid ${_profilegreen} 1px;
  text-align: center;
  background: ${_profilegreen};

  ${inversing({ color: truewhite, background: _profilegreen })};

  @media screen and (max-width: 540px) {
    margin-left: 0px;
  }
`
const Alert = styled.div`
  color: ${_formalert};
  margin-bottom: 15px;
  p {
    text-align: center;
  }
`
